import moment from "moment";

import type { ExchangeOrRefundPolicy } from "@busbud/core-types/dist/lib/grand-central/db";
import {
  PolicyTypes,
  RefundCreditType
} from "@busbud/core-types/dist/lib/grand-central/db";
import type {
  PublicSegment,
  PublicSegmentV4
} from "@busbud/core-types/dist/lib/poll-service/api";
import type { InternationalTime } from "@busbud/int-schemas/udm";

import { OUTBOUND_LEG_INDEX } from "@app/constants/trip";
import { isNotNil } from "@app/helpers/is-not-nil";
import { getCfarItem } from "@app/modules/account/components/trip-details/helpers";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";

import { getPurchaseTrip } from "./get-purchase-trip";
import { getOutboundLegOriginDestinationSegments } from "../udm/get-leg-origin-destination-segments";
import { getLegSegments } from "../udm/get-leg-segments";
import { isInFutureFromNow } from "../udm/international-time";

/**
 * Returns all the applicable refund policy for each segment of the outbound leg.
 * We can only cancel a roundtrip purchase if the outbound has not been traveled yet so no need to check for the return leg policies
 *
 * @param purchase
 */
export function getPurchaseApplicableRefundPolicies(
  purchase: NapiV2Purchase
): ExchangeOrRefundPolicy[] | null {
  // Can't refund a purchase that is not completed yet
  if (!purchase.completed_at) {
    return null;
  }

  const trip = getPurchaseTrip(purchase);

  const outbound_segments = getLegSegments(trip, OUTBOUND_LEG_INDEX);
  const {
    origin_segment: { departure_time }
  } = getOutboundLegOriginDestinationSegments(trip);

  if (!isInFutureFromNow(departure_time)) {
    return null;
  }

  // We can only cancel a roundtrip purchase if the outbound is not traveled yet so no need to check for the return leg policies
  const policies: ExchangeOrRefundPolicy[] = outbound_segments
    .map(segment =>
      getSegmentApplicableRefundPolicy(purchase.completed_at!, segment)
    )
    .filter(isNotNil);

  const cfar_refund_policy = getCfarRefundPolicy(purchase, departure_time);

  if (cfar_refund_policy) {
    return [cfar_refund_policy];
  }

  return policies.length > 0 ? policies : null;
}

export function getCfarRefundPolicy(
  purchase: NapiV2Purchase,
  departure_time: InternationalTime
): ExchangeOrRefundPolicy | null {
  const cfar_item = getCfarItem(purchase.items);

  if (cfar_item) {
    const cfar_refund_policy: ExchangeOrRefundPolicy = {
      type: PolicyTypes.SelfServe,
      flat_fee: null,
      flat_fee_currency: null,
      percent_fee: null,
      minimum_flat_fee: null,
      minimum_flat_fee_currency: null,
      cutoff_reference: "departure-date",
      cutoff_from: null,
      cutoff_to: cfar_item.ancillary_details.policies.cutoff_to,
      external_link: null,
      external_phone: null,
      external_email: null,
      credit_type: RefundCreditType.BusbudCredits
    };

    return getApplicableRefundPolicy(purchase.completed_at!, departure_time, [
      cfar_refund_policy
    ]);
  }

  return null;
}

function getSegmentApplicableRefundPolicy(
  purchased_at: string,
  segment: PublicSegment | PublicSegmentV4
): ExchangeOrRefundPolicy | null {
  const refund_policies = getSegmentRefundPolicies(segment);

  return getApplicableRefundPolicy(
    purchased_at,
    segment.departure_time,
    refund_policies
  );
}

function getApplicableRefundPolicy(
  purchased_at: string,
  departure_time: InternationalTime,
  refund_policies: ExchangeOrRefundPolicy[]
): ExchangeOrRefundPolicy | null {
  if (!refund_policies.length) {
    return null;
  }

  const departure_datetime = moment(departure_time.timestamp).tz(
    departure_time.timezone
  );
  const purchased_datetime = moment(purchased_at).tz(departure_time.timezone);
  const now = moment().tz(departure_time.timezone);

  if (departure_datetime < now) {
    return null; // Departure is in the past
  }

  const purchase_date_policies = refund_policies.filter(policy =>
    isPolicyValidForDate("purchase-date", purchased_datetime, now, policy)
  );
  const departure_date_policies = refund_policies.filter(policy =>
    isPolicyValidForDate("departure-date", departure_datetime, now, policy)
  );

  if (purchase_date_policies.length > 1 || departure_date_policies.length > 1) {
    return null; // Multiple applicable policies for a single cutoff_reference are not allowed
  }

  // If multiple policies apply, prefer purchase_date ones as they will have lower fees
  const [valid_policy] = [
    ...purchase_date_policies,
    ...departure_date_policies
  ];

  return valid_policy || null;
}

function getSegmentExpiredRefundPolicy(
  purchased_at: string,
  segment: PublicSegment | PublicSegmentV4
): ExpiredPoliciesByType {
  const refund_policies = getSegmentRefundPolicies(segment);

  const { timezone, timestamp } = segment.departure_time;
  const departure_datetime = moment(timestamp).tz(timezone);
  const purchased_datetime = moment(purchased_at).tz(timezone);
  const now = moment().tz(timezone);

  const purchase_date_policies = refund_policies.filter(
    policy =>
      isPurchaseDateRefundPolicy(policy) &&
      !isPolicyValidForDate("purchase-date", purchased_datetime, now, policy)
  );
  const departure_date_policies = refund_policies.filter(
    policy =>
      isDepartureDateRefundPolicy(policy) &&
      !isPolicyValidForDate("departure-date", departure_datetime, now, policy)
  );

  return {
    // For departure-date policies, we want to return the policy with the highest cutoff_to aka. the one the furthest from the departure time
    expired_departure_date_policy: getPolicyWithHighestCutoffTo(
      departure_date_policies
    ),
    // For purchase-date policies, we want to return the policy with the lowest cutoff_to aka. the one the closest from the purchase date
    expired_purchase_date_policy: getPolicyWithShortestCutoffTo(
      purchase_date_policies
    )
  };
}

function getPolicyWithShortestCutoffTo(
  policies: ExchangeOrRefundPolicy[]
): ExchangeOrRefundPolicy | null {
  const sorted_policies_with_cutoff_to = policies
    .filter(({ cutoff_to }) => isNotNil(cutoff_to))
    .sort((a, b) => a.cutoff_to! - b.cutoff_to!);
  return sorted_policies_with_cutoff_to[0] || null;
}

function getPolicyWithHighestCutoffTo(
  policies: ExchangeOrRefundPolicy[]
): ExchangeOrRefundPolicy | null {
  const sorted_policies_with_cutoff_to = policies
    .filter(({ cutoff_to }) => isNotNil(cutoff_to))
    .sort((a, b) => b.cutoff_to! - a.cutoff_to!);
  return sorted_policies_with_cutoff_to[0] || null;
}

function getSegmentRefundPolicies(
  segment: PublicSegment | PublicSegmentV4
): ExchangeOrRefundPolicy[] {
  // TODO: UDMV4
  // @ts-expect-error fix fare
  return segment.fare.terms.refund_policies.filter(
    // @ts-expect-error fix fare
    ({ type }) => ![PolicyTypes.NoExchange, PolicyTypes.NoRefund].includes(type)
  );
}

type ExpiredPoliciesByType = {
  expired_departure_date_policy: ExchangeOrRefundPolicy | null;
  expired_purchase_date_policy: ExchangeOrRefundPolicy | null;
};
/**
 * Returns the expired policy with the cutoff_to the closest to the departure_date or purchase_date
 *
 * @param purchase
 */
export function getPurchaseExpiredPoliciesByType(
  purchase: NapiV2Purchase
): ExpiredPoliciesByType {
  const init_results: ExpiredPoliciesByType = {
    expired_departure_date_policy: null,
    expired_purchase_date_policy: null
  };

  if (!purchase.completed_at) {
    return init_results;
  }

  const trip = getPurchaseTrip(purchase);

  const outbound_segments = getLegSegments(trip, OUTBOUND_LEG_INDEX);

  return outbound_segments
    .map(segment =>
      getSegmentExpiredRefundPolicy(purchase.completed_at!, segment)
    )
    .reduce((results, segment_results) => {
      return {
        expired_departure_date_policy: getPolicyWithMaxCutoff(
          results.expired_departure_date_policy,
          segment_results.expired_departure_date_policy
        ),
        expired_purchase_date_policy: getPolicyWithMinCutoff(
          results.expired_purchase_date_policy,
          segment_results.expired_purchase_date_policy
        )
      };
    }, init_results);
}

function getPolicyWithMinCutoff(
  policy_1: ExchangeOrRefundPolicy | null,
  policy_2: ExchangeOrRefundPolicy | null
): ExchangeOrRefundPolicy | null {
  const cutoff_1 = policy_1?.cutoff_to ?? Infinity;
  const cutoff_2 = policy_2?.cutoff_to ?? Infinity;

  if (cutoff_1 < cutoff_2) {
    return policy_1;
  }

  if (cutoff_2 < cutoff_1) {
    return policy_2;
  }

  if (cutoff_1 === cutoff_2 && cutoff_1 !== Infinity) {
    return policy_1;
  }

  return null;
}

function getPolicyWithMaxCutoff(
  policy_1: ExchangeOrRefundPolicy | null,
  policy_2: ExchangeOrRefundPolicy | null
): ExchangeOrRefundPolicy | null {
  const cutoff_1 = policy_1?.cutoff_to ?? -Infinity;
  const cutoff_2 = policy_2?.cutoff_to ?? -Infinity;

  if (cutoff_1 > cutoff_2) {
    return policy_1;
  }

  if (cutoff_2 > cutoff_1) {
    return policy_2;
  }

  if (cutoff_1 === cutoff_2 && cutoff_1 !== -Infinity) {
    return policy_1;
  }

  return null;
}

const operation_map = {
  "departure-date": "subtract",
  "purchase-date": "add"
} as const;
function isPolicyValidForDate(
  cutoff_reference: "departure-date" | "purchase-date",
  reference_datetime: moment.Moment,
  now: moment.Moment,
  policy: ExchangeOrRefundPolicy
) {
  // Disregard policies that are not of the cutoff reference we are targeting
  if (policy.cutoff_reference !== cutoff_reference) {
    return false;
  }

  const operation = operation_map[cutoff_reference];

  const cutoff_from_included =
    policy.cutoff_from === null ||
    now >= reference_datetime.clone()[operation](policy.cutoff_from, "hours");
  const cutoff_to_included =
    policy.cutoff_to === null ||
    now < reference_datetime.clone()[operation](policy.cutoff_to, "hours");

  return cutoff_from_included && cutoff_to_included;
}

function isPurchaseDateRefundPolicy(policy: ExchangeOrRefundPolicy): boolean {
  return policy.cutoff_reference === "purchase-date";
}

function isDepartureDateRefundPolicy(policy: ExchangeOrRefundPolicy): boolean {
  return policy.cutoff_reference === "departure-date";
}
